import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { AuthorityBadge } from "./authority-badge";
import { getAuthorityStyle } from "./authority-colors";
import { trpc } from "@/lib/trpc";
import { FileText, BookOpen, Palette } from "lucide-react";

interface CitationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceType: string | null;
  sourceId: string | null;
  projectId: string;
}

export function CitationPanel({
  open,
  onOpenChange,
  sourceType,
  sourceId,
  projectId,
}: CitationPanelProps) {
  const { data, isLoading } = trpc.walkthrough.getCitationDetail.useQuery(
    {
      sourceType: sourceType as "chunk",
      sourceId: sourceId ?? "",
      projectId,
    },
    {
      enabled: open && !!sourceType && !!sourceId,
    },
  );

  const style = data ? getAuthorityStyle(data.authorityLevel) : null;
  const Icon = style?.icon;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {isLoading ? (
          <SheetHeader>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-4 w-48" />
            <div className="mt-4 space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          </SheetHeader>
        ) : data ? (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2">
                {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                <SheetTitle className="text-base">Source excerpt</SheetTitle>
              </div>
              <SheetDescription className="flex items-center gap-2">
                <AuthorityBadge level={data.authorityLevel} />
                {data.sourceCategory === "design_system" && (
                  <Badge
                    variant="outline"
                    className="gap-1 text-primary border-primary/40"
                  >
                    <Palette className="h-3 w-3" />
                    Design system
                  </Badge>
                )}
                <span>{data.sourceName}</span>
                {data.sourceVersionLabel && (
                  <span className="text-xs text-muted-foreground">
                    ({data.sourceVersionLabel})
                  </span>
                )}
              </SheetDescription>
            </SheetHeader>

            <div className="space-y-5 px-4 pb-6">
              {/* Full content */}
              <div>
                <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                  <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  Content
                </h4>
                <div className="rounded-lg border bg-card p-3">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {data.content}
                  </p>
                </div>
              </div>

              {/* Evidence chunks */}
              {data.evidenceChunks.length > 0 && (
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    Original evidence
                  </h4>
                  <div className="space-y-2">
                    {data.evidenceChunks.map((chunk) => (
                      <div
                        key={chunk.id}
                        className="rounded-lg border border-dashed bg-muted/30 p-3"
                      >
                        <p className="text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap">
                          {chunk.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Version info */}
              {data.sourceVersionDate && (
                <div className="text-xs text-muted-foreground">
                  Version date:{" "}
                  {new Date(data.sourceVersionDate).toLocaleDateString(
                    undefined,
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    },
                  )}
                </div>
              )}

              {/* Cross-references */}
              {data.crossReferenceCount > 0 && (
                <div className="rounded-lg border bg-muted/30 px-3 py-2">
                  <p className="text-xs text-muted-foreground">
                    Referenced by{" "}
                    <span className="font-medium text-foreground">
                      {data.crossReferenceCount}
                    </span>{" "}
                    finding{data.crossReferenceCount !== 1 ? "s" : ""} in this
                    project
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <SheetHeader>
            <SheetTitle>Citation not found</SheetTitle>
            <SheetDescription>
              The referenced knowledge item could not be loaded.
            </SheetDescription>
          </SheetHeader>
        )}
      </SheetContent>
    </Sheet>
  );
}
